import type { ApiClient } from "../../api/ApiClient";
import { ApiError, AuthenticationRequiredError, NetworkError } from "../../api/errors";
import { clientLogReceiptSchema } from "../../api/schemas";
import type { ClientLogBatch, ClientLogDelivery } from "./clientLog";

/** Path of the reports (`POST /api/v1/client-logs`). */
export const clientLogPath = "/api/v1/client-logs";

/**
 * Sends reports to the API (F-68): with the session of a signed-in person, so the operator sees the
 * tenant, otherwise or when the session is not usable without one. The request may outlive the
 * page.
 *
 * @param api - The API client.
 * @param signedIn - Returns whether a person is signed in.
 * @param batch - The reports.
 * @returns `retry` without a connection, `dropped` when the API refused the reports, otherwise
 *   `delivered`.
 */
export async function deliverClientLogs(
  api: ApiClient,
  signedIn: () => Promise<boolean>,
  batch: ClientLogBatch,
): Promise<ClientLogDelivery> {
  const send = async (anonymous: boolean): Promise<ClientLogDelivery> => {
    await api.post(clientLogPath, batch, clientLogReceiptSchema, { anonymous, keepalive: true });
    const delivered: ClientLogDelivery = "delivered";
    return delivered;
  };
  let outcome: ClientLogDelivery;

  try {
    const withSession = await signedIn();

    try {
      outcome = await send(!withSession);
    } catch (error) {
      const sessionRefused =
        withSession &&
        (error instanceof AuthenticationRequiredError ||
          (error instanceof ApiError && (error.status === 401 || error.status === 403)));

      if (!sessionRefused) {
        throw error;
      }

      outcome = await send(true);
    }
  } catch (error) {
    outcome = error instanceof NetworkError ? "retry" : "dropped";
  }

  return outcome;
}
